import { tsr } from '../../lib/api.js';
import { HomeCard } from './HomeCard.js';
import { dateOnlyToDate, useToday } from './useToday.js';

const AXIS_MAX_HOURS = 12;
const AXIS_TICKS = [12, 8, 4, 0];
const WINDOW_DAYS = 7;
/** Height, in % of the plot, of the stub drawn for a day marked absent, so it reads as a day. */
const ABSENT_STUB_PERCENT = 6;

/** `YYYY-MM-DD`, `days` before `dateOnly` — calendar arithmetic only, no zones. */
function daysBefore(dateOnly: string, days: number): string {
  const d = dateOnlyToDate(dateOnly);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * The last seven days of the caller's worked hours, straight from their status report
 * (`GET /attendance/status-report`). Overtime is stacked on top of the regular hours the way the
 * engine defines it: `workHours` already includes overtime, which is the part beyond the shift's
 * full-day threshold, so the regular bar is the difference. Weekends and holidays draw no bar.
 */
export function AttendanceTrendChart() {
  const today = useToday();
  const startDate = today ? daysBefore(today, WINDOW_DAYS - 1) : undefined;
  const query = tsr.attendance.getMyStatusReport.useQuery({
    queryKey: ['attendance', 'status-report', 'home-trend', startDate, today],
    queryData: { query: { startDate, endDate: today } },
    enabled: Boolean(today),
  });

  const days =
    query.data?.status === 200 && startDate && today
      ? query.data.body.days
          .filter((d) => d.rawDate >= startDate && d.rawDate <= today)
          .sort((a, b) => a.rawDate.localeCompare(b.rawDate))
      : [];

  return (
    <HomeCard
      title="Attendance Status"
      viewAll={{ to: '/attendance/status-report', label: 'Last 7 days' }}
      query={{ isPending: !today || query.isPending, isError: query.isError }}
      isEmpty={days.length === 0}
      emptyTitle="No attendance yet"
      emptyDescription="Your worked hours for the last 7 days will show here."
    >
      <div className="flex gap-3 pt-2">
        <div className="flex h-36 flex-col justify-between text-right text-[10px] font-semibold tabular-nums text-fg-subtle">
          {AXIS_TICKS.map((t) => (
            <span key={t}>{t}h</span>
          ))}
        </div>
        <div className="relative flex h-36 flex-1 items-end gap-3 border-b border-border pb-0.5">
          {AXIS_TICKS.slice(0, -1).map((t) => (
            <span
              key={t}
              className="pointer-events-none absolute inset-x-0 border-t border-border"
              style={{ top: `${100 - (t / AXIS_MAX_HOURS) * 100}%` }}
              aria-hidden="true"
            />
          ))}
          {days.map((day) => {
            const overtime = Math.min(day.overtimeHours, day.workHours);
            const regular = Math.max(0, day.workHours - overtime);
            const absent = day.status === 'Absent';
            const percent = (hours: number) => `${Math.min(100, (hours / AXIS_MAX_HOURS) * 100)}%`;
            return (
              <div
                key={day.rawDate}
                className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-0.5"
                title={`${day.date}: ${day.workHours.toFixed(1)}h${absent ? ' (absent)' : ''}`}
              >
                {overtime > 0 && (
                  <div
                    className="w-full max-w-7 rounded-t-sm bg-info/20"
                    style={{ height: percent(overtime) }}
                  />
                )}
                {regular > 0 && (
                  <div
                    className="w-full max-w-7 rounded-t-xs bg-accent"
                    style={{ height: percent(regular) }}
                  />
                )}
                {absent && (
                  <div
                    className="w-full max-w-7 rounded-t-xs bg-border"
                    style={{ height: `${ABSENT_STUB_PERCENT}%` }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex gap-3 pl-7 text-center text-[10px] font-medium text-fg-muted">
        {days.map((day) => (
          <span key={day.rawDate} className="min-w-0 flex-1">
            {day.dayName.slice(0, 3)} {day.rawDate.slice(8)}
          </span>
        ))}
      </div>

      <ul className="mt-4 flex items-center gap-5 border-t border-border pt-3 text-[11px] text-fg-muted">
        <li className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-accent" aria-hidden="true" />
          Present
        </li>
        <li className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-info/20" aria-hidden="true" />
          Overtime
        </li>
        <li className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-border" aria-hidden="true" />
          Absent
        </li>
      </ul>
    </HomeCard>
  );
}
